import { AuthenticationResult, IPublicClientApplication, SilentRequest } from '@azure/msal-browser';
import React from 'react';
import { checkValidResponse, scopeApi } from './common';

export interface Message {
    text: string;
    isUser: boolean;
    fullResponse?: string;
    id: string;
};
export interface ChatProps {
    inputValue: string;
    deleteChatHandler: React.MouseEventHandler<HTMLButtonElement>;
    isLoading: boolean;
    submitHandler: React.FormEventHandler<HTMLFormElement>;
    messages: Message[];
    setInputHandler: React.ChangeEventHandler<HTMLInputElement>;
};
export interface SetMessagesHandler {
    (messages: React.SetStateAction<Message[]>): void;
};
export interface SetCharIdHandler {
    (messages: React.SetStateAction<string>): void;
};

export const authenticateChat = async (instance: IPublicClientApplication): Promise<AuthenticationResult> => {
    const activeAccount = instance.getActiveAccount();
    const shouldForceRefresh = !activeAccount.idTokenClaims['email'];
    const isExpired = activeAccount.idTokenClaims.exp < (Date.now() / 1000);
    const silentRequest = {
        account: activeAccount,
        scopes: scopeApi,
        forceRefresh: shouldForceRefresh || isExpired,
        claims: "email"
    } as const satisfies SilentRequest;
    return await instance.acquireTokenSilent(silentRequest);
};

export const loadChatHistory = async (forceNewChat: boolean, chatId: string, chatIdHandler: SetCharIdHandler, instance: IPublicClientApplication, useDetailedLog: boolean): Promise<Message[]> => {
    let tempChatId = chatId;
    if (!tempChatId || forceNewChat) {
        tempChatId = await createNewChat(instance, useDetailedLog);
        chatIdHandler(tempChatId);
    }
    const activeAccount = instance.getActiveAccount();
    const tokenResponse = await authenticateChat(instance);
    const messageObj = JSON.stringify({
        chatId: tempChatId
    });
    const response = await fetch('/chat/getChatHistory', {
        method: 'POST',
        headers: new Headers({
            'Authorization': 'Bearer ' + tokenResponse.accessToken,
            'xct-user-context': activeAccount.idToken,
            'Content-Type': 'application/json'
        }),
        body: messageObj
    });
    await checkValidResponse(response, "loadChatHistory");
    const data = await response.json();
    return data.map(x => ({ text: x.content, isUser: x.role == "user", id: x.id }));
};

export const createNewChat = async (instance: IPublicClientApplication, useDetailedLog: boolean): Promise<string> => {
    const activeAccount = instance.getActiveAccount();
    const tokenResponse = await authenticateChat(instance);
    const response = await fetch('/chat/startChat', {
        method: 'POST',
        headers: new Headers({
            'Authorization': 'Bearer ' + tokenResponse.accessToken,
            'xct-user-context': activeAccount.idToken,
            'xct-user-use-log-details': useDetailedLog ? 'true' : 'false'
        })
    });
    await checkValidResponse(response, "createNewChat");
    const data = await response.json();
    return data.id;
};

export const addNewMessage = async (chatId: string, chatIdHandler: SetCharIdHandler, e: React.FormEvent, inputValue: string, setMessages: SetMessagesHandler, instance: IPublicClientApplication): Promise<Message> => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const newMessage: Message = {
        text: inputValue,
        isUser: true,
        id: Date.now().toString()
    };

    setMessages(prev => [...prev, newMessage]);
    let tempChatId = chatId;
    if (!chatId) {
        tempChatId = await createNewChat(instance, false);
        chatIdHandler(tempChatId);
    }
    const activeAccount = instance.getActiveAccount();
    const tokenResponse = await authenticateChat(instance);
    const messageObj = JSON.stringify({
        chatId: tempChatId, message: inputValue
    });
    const response = await fetch('/chat/sendMessage', {
        method: 'POST',
        headers: new Headers({
            'Authorization': 'Bearer ' + tokenResponse.accessToken,
            'xct-user-context': activeAccount.idToken,
            'Content-Type': 'application/json'
        }),
        body: messageObj
    });
    await checkValidResponse(response, "addNewMessage");
    const data = await response.json();
    const result: Message = {
        text: data.output,
        fullResponse: data.fullResponse,
        isUser: false,
        id: Date.now().toString()
    };
    return result;
};
